import React from 'react';
import { Paper, Typography } from '@mui/material';
import { useRooftop, useUser } from '@store';
import { useBusinessHours } from '../hooks/useBusinessHours';
import { ScheduleEditor } from '@components/Schedule';
import type { Businesshours_Insert_Input } from '@gql/base-types';
import { getRooftopTimezone } from '@utils/timezoneUtils';

const BusinessHours: React.FC = () => {
  const rooftop = useRooftop();
  const user = useUser();
  const locationId = rooftop?.location?.id?.toString();
  const timezone = getRooftopTimezone(rooftop);
  const userName = user?.profile?.display_name || 'Unknown';

  const { businessHours, createBusinessHours, updateBusinessHours, deleteBusinessHours, loading, refetch } =
    useBusinessHours(locationId);

  // Wrapper function to adapt ScheduleEditor's simplified interface to GraphQL mutation
  const handleCreate = async (schedules: Businesshours_Insert_Input[]) => {
    await createBusinessHours({ variables: { businessHours: schedules } });
  };

  if (!locationId) {
    return (
      <Paper sx={{ padding: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Please select a location to manage business hours.
        </Typography>
      </Paper>
    );
  }

  return (
    <ScheduleEditor
      config={{
        entityType: 'location',
        entityId: locationId,
        timezone: timezone,
        labels: {
          title: 'Weekly Hours',
          closed: 'Closed',
          openLabel: 'Opens',
          closeLabel: 'Closes',
          unavailableText: (dayName) => `Location is closed on ${dayName}s`,
        },
        types: {
          specific: 'business-hours',
          default: 'default-hours',
        },
        naming: (dayName) => `Business Hours - ${dayName}`,
      }}
      scheduleData={businessHours}
      onCreate={handleCreate}
      onUpdate={updateBusinessHours}
      onDelete={deleteBusinessHours}
      loading={loading}
      userName={userName}
      onRefetch={() => refetch()}
    />
  );
};

export default BusinessHours;
